// ============================================================
// accounts_toolbar_controller.js — /accounts 페이지 정렬·보기 toolbar UX
// ============================================================
// 역할:
//   1) view 토글 스위치(grid ↔ group) 클릭 시 즉시 스위치 상태 반영 (서버 응답 대기 X)
//   2) sort select 변경 시 spinner 표시
//   3) turbo:frame-render(#slot-list) 이벤트 수신 시 spinner 자동 숨김
//
// 사용 위치: app/views/accounts/_filter_tabs.html.erb 의 toolbar 부근
//
// 타깃 (data-accounts-toolbar-target):
//   - viewSwitch : 보기 모드 토글 스위치 (role="switch" 링크)
//   - spinner    : 로딩 스피너 div
//
// 액션:
//   - click->accounts-toolbar#switchView    : view 토글 (viewSwitch)
//   - change->accounts-toolbar#changeSort   : sort select onchange
//
// 데이터:
//   스위치는 data-view-mode 에 "이동할 반대 모드"(grid 또는 group)를 갖는다.
//   2026-09-06: 버튼 2개(gridBtn/groupBtn)에서 스위치 1개로 교체됐다.
// ============================================================

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["viewSwitch", "spinner"]

  // 컨트롤러 connect 시 Turbo Frame 렌더 완료 이벤트 구독
  connect() {
    this._onFrameRender = (e) => {
      // #slot-list 프레임의 렌더 완료만 처리 (다른 프레임 이벤트는 무시)
      if (e.target && e.target.id === "slot-list") {
        this._hideSpinner()
      }
    }
    document.addEventListener("turbo:frame-render", this._onFrameRender)

    // 추가 안전망 — Turbo navigation 직후에도 spinner 가 남지 않도록 turbo:load 에서 숨김
    this._onTurboLoad = () => this._hideSpinner()
    document.addEventListener("turbo:load", this._onTurboLoad)
  }

  disconnect() {
    document.removeEventListener("turbo:frame-render", this._onFrameRender)
    document.removeEventListener("turbo:load", this._onTurboLoad)
  }

  // ─────────────────────────────────────────────────────────
  // view 토글 스위치 클릭 — 즉시 스위치 넘김 + spinner 표시
  // 기본 link 동작은 그대로 (Turbo 가 navigation 처리)
  // ─────────────────────────────────────────────────────────
  switchView(event) {
    const btn = event.currentTarget
    const newView = btn.dataset.viewMode  // 이동할 모드: "grid" 또는 "group"
    if (!newView) return

    this._setSwitchState(newView)
    this._showSpinner()
    // event.preventDefault() 안 함 — 그래야 link_to 가 정상적으로 GET 요청 보냄
  }

  // ─────────────────────────────────────────────────────────
  // sort select 변경 — 즉시 spinner 표시
  // form 의 onchange="this.form.requestSubmit()" 이 별도로 동작하여 form 제출.
  // 본 핸들러는 spinner 토글만 담당.
  // ─────────────────────────────────────────────────────────
  changeSort() {
    this._showSpinner()
  }

  // ─────────────────────────────────────────────────────────
  // 내부 헬퍼: 스위치 상태 토글
  // CSS 클래스 ft-view-switch--on 으로 앱 그룹 상태 표현. inline style 은 건드리지 않음.
  // aria-checked 도 함께 갱신한다 — 스크린리더가 즉시 바뀐 상태를 읽어야 한다.
  // ─────────────────────────────────────────────────────────
  _setSwitchState(view) {
    if (!this.hasViewSwitchTarget) return
    const isGroup = view === "group"
    this.viewSwitchTarget.classList.toggle("ft-view-switch--on", isGroup)
    this.viewSwitchTarget.setAttribute("aria-checked", String(isGroup))
  }

  _showSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.style.display = "inline-flex"
    }
  }

  _hideSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.style.display = "none"
    }
  }
}
